import React, { useState, useEffect } from "react";
import {
  SFTypography,
  SFStack,
  SFUseStyled,
  SFGridContainer,
  SFGridItem,
  SFHelpers,
} from "@sf-core";
import ProgressCards from "./progressCards";
import { useTranslation } from "react-i18next";
import ProgressChart from "./progressChart";
import { DOMAIN_TYPES } from "../../constants/domainTypes";
import { countHoursPerMonths } from "./utils/countHoursPerMonths";

const { styled } = SFUseStyled();

const HeroWrapper = styled("div")(({ theme }) => ({
  position: "relative",
  height: 335,
  borderRadius: "6px",
  margin: 0,
  padding: 0,
  boxShadow: "0px 0px 15px 0px rgba(0,0,0,0.75)",
}));

const StyledGridContainer = styled(SFGridContainer)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  height: "100%",
  zIndex: 2,
}));

const GradientOverlay = styled("div")(({ theme }) => ({
  height: "100%",
  background:
    "linear-gradient(90deg, rgba(0, 0, 0, 0.2) 24.48%, rgba(0, 0, 0, 0.1) 72.92%)",
  backgroundSize: "cover",
  overflowY: "auto",
  overflowX: "hidden",
  zIndex: 1,
  borderRadius: theme.spacing(1),
}));

const StyledStack = styled(SFStack)(({ theme }) => ({
  minWidth: 42,
  height: 42,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0, 1),
  background:
    "linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.05) 100%)",
}));

const SFStyledStack = styled(SFStack)(({ theme }) => ({
  width: "100%",
  height: "100%",
  paddingTop: theme.spacing(2),
}));

const StyledVideo = styled("video")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  zIndex: -1,
  borderRadius: theme.spacing(1),
}));

const CardsContainer = styled(SFStack)(({ theme }) => ({
  padding: theme.spacing(2, 1),
}));

const StatItem = ({ value, title }) => (
  <SFStack direction="row" spacing={1} alignItems="center">
    <StyledStack alignItems="center" justifyContent="center">
      <SFTypography variant="academyCardTitle">{value}</SFTypography>
    </StyledStack>
    <SFTypography variant="body1">{title}</SFTypography>
  </SFStack>
);

const SFProgressHero = ({ hero }) => {
  const { t } = useTranslation();
  const [heroData, setHeroData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [totalHours, setTotalHours] = useState(0);

  useEffect(() => {
    let academies = [];
    let jobs = [];
    let skills = [];

    // Divide the user domains into academies, jobs, and skills
    hero.userDomains.map((item) => {
      switch (item.type) {
      case DOMAIN_TYPES.ACADEMY:
        academies.push(item);
        break;
      case DOMAIN_TYPES.USER_SELECTED_JOB:
      case DOMAIN_TYPES.USER_JOB_GOAL:
        jobs.push(item);
        break;
      case DOMAIN_TYPES.USER_SKILL_GOAL:
        skills.push(item);
        break;
      }
    });
    setHeroData([{ 0: academies }, { 1: jobs }, { 2: skills }]);
  }, [hero.userDomains]);

  useEffect(() => {
    // Calculate the learning hours for each month and the total hours for the last 12 months
    let { processedData, totalChartHours } = countHoursPerMonths(
      hero.last12MonthsProgress
    );
    // Calculate the learning hours for for all months
    let allMonthsTotalHours = SFHelpers.DurationFormatter(
      hero.totalProgress.totalCompletedDuration,
      SFHelpers.DurationFormatterOptions.HOURS_ONLY
    );

    setChartData(processedData);
    // Set the total hours to the maximum of the total hours for last 12 months and the total hours for all months
    setTotalHours(
      totalChartHours > allMonthsTotalHours
        ? totalChartHours
        : allMonthsTotalHours
    );
  }, [hero.last12MonthsProgress]);

  return (
    <HeroWrapper>
      <StyledVideo autoPlay loop muted src={hero.image} />
      <GradientOverlay>
        <StyledGridContainer spacing={2}>
          <SFGridItem xs={12} md={5} lg={5}>
            <CardsContainer direction="column" spacing={2}>
              <SFTypography variant="heroTitle">
                {t("homeScreen.heroes.progressTitle")}
              </SFTypography>
              {heroData && <ProgressCards data={heroData} />}
            </CardsContainer>
          </SFGridItem>
          <SFGridItem xs={12} md={7} lg={7}>
            <SFStyledStack spacing={4}>
              <SFStack direction="row" justifyContent="space-around">
                <StatItem
                  value={totalHours}
                  title={t("homeScreen.heroes.progressStats.totalHours")}
                />
                <StatItem
                  value={hero.totalProgress.startedSkillPaths}
                  title={t("homeScreen.heroes.progressStats.startedSkillPaths")}
                />
                <StatItem
                  value={hero.totalProgress.completedSkillPaths}
                  title={t(
                    "homeScreen.heroes.progressStats.completedSkillPaths"
                  )}
                />
                <StatItem
                  value={hero.totalProgress.totalCoursesCompleted}
                  title={t(
                    "homeScreen.heroes.progressStats.totalCoursesCompleted"
                  )}
                />
              </SFStack>
              {chartData && <ProgressChart data={chartData} />}
            </SFStyledStack>
          </SFGridItem>
        </StyledGridContainer>
      </GradientOverlay>
    </HeroWrapper>
  );
};

export default SFProgressHero;
